import { Avatar } from '@mui/material';

/**
 * Initials for a display name, at most two letters.
 *
 * First and last word rather than the first two, so "Grace Brewster Hopper" is
 * GH — the middle name is the least identifying part, and a three-word name is
 * common enough that taking the first two would routinely produce the wrong
 * pair.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]![0]!;
  const last = words.length > 1 ? words[words.length - 1]![0]! : '';
  return (first + last).toUpperCase();
}

interface TeamsAvatarProps {
  /** Display name — drives the initials fallback and the accessible name. */
  name: string;
  /**
   * The person's Entra object id. Absent for app-attributed messages, where we
   * genuinely do not know whose face to show.
   */
  entraOid?: string;
  /**
   * False whenever the connection's token lacks `User.ReadBasic.All` — which is
   * every connection made before it was consented on 2026-07-23, since a grant
   * does not upgrade a token already issued. Must be respected rather than
   * optimistically fetching: a request that will be refused turns into a broken
   * image on every message, which is worse than the initials it replaced.
   */
  avatarsAvailable?: boolean;
  size?: number;
}

/**
 * A person's Teams profile photo, falling back to their initials.
 *
 * The photo is fetched through the ATLAS proxy rather than from Graph directly:
 * an `<img>` sends no authorization header, and Graph's photo endpoint requires
 * one. The proxy answers 204 when somebody has no photo — the common case — and
 * MUI renders the child initials whenever the image fails or is empty, so no
 * error handling is needed here for that path.
 */
export function TeamsAvatar({
  name,
  entraOid,
  avatarsAvailable = false,
  size = 28,
}: TeamsAvatarProps) {
  const src =
    avatarsAvailable && entraOid
      ? `/api/v1/teams/avatars/${encodeURIComponent(entraOid)}`
      : undefined;

  return (
    <Avatar
      src={src}
      // `alt` is deliberately empty: the author's name is already rendered as
      // text beside this, and a screen reader announcing it twice is noise.
      alt=""
      aria-hidden
      sx={{ width: size, height: size, fontSize: size * 0.4, flexShrink: 0 }}
    >
      {initialsOf(name)}
    </Avatar>
  );
}
